
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  name: z.string().min(1, "Academic year name is required"),
  term1Start: z.string().min(1, "Term 1 start date is required"),
  term1End: z.string().min(1, "Term 1 end date is required"),
  term2Start: z.string().min(1, "Term 2 start date is required"),
  term2End: z.string().min(1, "Term 2 end date is required"),
  term3Start: z.string().min(1, "Term 3 start date is required"),
  term3End: z.string().min(1, "Term 3 end date is required"),
}).refine((data) => {
  // Parse dates properly, ensuring we use the same format consistently
  function parseDate(dateStr) {
    // Create date object and reset the time to 00:00:00
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date;
  }
  
  const term1Start = parseDate(data.term1Start);
  const term1End = parseDate(data.term1End);
  const term2Start = parseDate(data.term2Start);
  const term2End = parseDate(data.term2End);
  const term3Start = parseDate(data.term3Start);
  const term3End = parseDate(data.term3End);

  // Validate that end dates are after start dates
  if (term1End <= term1Start) return false;
  if (term2End <= term2Start) return false;
  if (term3End <= term3Start) return false;

  return true;
}, {
  message: "End date must be after start date for each term."
});

type FormValues = z.infer<typeof formSchema>;

type EditAcademicYearFormProps = {
  academicYear: any;
  onSuccess: () => void;
  onClose: () => void;
};

export function EditAcademicYearForm({ academicYear, onSuccess, onClose }: EditAcademicYearFormProps) {
  const { toast } = useToast();
  
  // Sort terms by term number extracted from name
  const sortedTerms = [...academicYear.terms].sort((a, b) => {
    const termNumberA = parseInt(a.name.split(' ')[1]);
    const termNumberB = parseInt(b.name.split(' ')[1]);
    return termNumberA - termNumberB;
  });

  // Ensure we have exactly 3 terms
  if (sortedTerms.length !== 3) {
    console.error("Expected exactly 3 terms, got", sortedTerms.length);
  }

  // Map to correct indices
  const term1 = sortedTerms[0] || {};
  const term2 = sortedTerms[1] || {};
  const term3 = sortedTerms[2] || {};
  
  // Format dates consistently for the form inputs
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Format as YYYY-MM-DD for input type="date"
    return date.toISOString().split('T')[0];
  };

  const defaultValues = {
    name: academicYear.name,
    term1Start: formatDateForInput(term1.start_date),
    term1End: formatDateForInput(term1.end_date),
    term2Start: formatDateForInput(term2.start_date),
    term2End: formatDateForInput(term2.end_date),
    term3Start: formatDateForInput(term3.start_date),
    term3End: formatDateForInput(term3.end_date),
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const onSubmit = async (values) => {
    try {
      // Update academic year name
      const { error: yearError } = await supabase
        .from("academic_years")
        .update({ name: values.name })
        .eq("id", academicYear.id);

      if (yearError) throw yearError;

      // Update terms with correct mapping
      const terms = [
        {
          id: term1.id,
          start_date: values.term1Start,
          end_date: values.term1End,
        },
        {
          id: term2.id,
          start_date: values.term2Start,
          end_date: values.term2End,
        },
        {
          id: term3.id,
          start_date: values.term3Start,
          end_date: values.term3End,
        },
      ];

      // Update terms one by one
      for (const term of terms) {
        console.log("Updating term:", term.id, "with dates:", term.start_date, term.end_date);
        const { error: termError } = await supabase
          .from("terms")
          .update({
            start_date: term.start_date,
            end_date: term.end_date,
          })
          .eq("id", term.id);

        if (termError) {
          console.error("Error updating term:", termError);
          throw termError;
        }
      }

      toast({
        title: "Success",
        description: "Academic year updated successfully",
      });
      
      onClose();
      onSuccess();
    } catch (error) {
      console.error("Error updating academic year:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to update academic year",
        variant: "destructive",
      });
    }
  };

  return (
    <DialogContent className="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>Edit Academic Year</DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Academic Year Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., 2024-2025" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="term1Start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Term 1 Start</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="term1End"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Term 1 End</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="term2Start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Term 2 Start</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="term2End"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Term 2 End</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="term3Start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Term 3 Start</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="term3End"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Term 3 End</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Button type="submit" className="w-full">
            Update Academic Year
          </Button>
        </form>
      </Form>
    </DialogContent>
  );
}
